/** Unione delle conversazioni Mirakl dello stesso ordine — SOLO presentazione.
 *
 * Su Mirakl lo stesso cliente apre PIÙ conversazioni per lo stesso ordine e
 * l'operatore le vede come card separate, perdendo il filo.
 *
 * ⛔ NON è un bug di dedup e non si risolve nel DB: ``UniqueConstraint`` è su
 * ``(account_id, external_thread_id)`` e ``external_order_id`` non è unico —
 * N thread sullo stesso ordine sono legittimi e attesi. Il modello dati è
 * corretto così; quello che va unito è la VISTA.
 *
 * Funzioni pure, stesso stile di ``messageSide.ts``: si testano senza render.
 */
import type { ThreadDetail, ThreadListItem, ThreadMessage } from "./api";

/** Riga di lista, eventualmente frutto di più conversazioni fuse. */
export type GroupedThread = ThreadListItem & {
  /** Id REALI dei thread fusi, dal più recente al più vecchio.
   *  Un solo elemento = thread singolo, nessun raggruppamento. */
  groupThreadIds: number[];
};

/** Messaggio annotato con la conversazione da cui viene: serve al separatore
 *  "nuova conversazione" e a dire all'operatore a cosa sta rispondendo. */
export type MergedMessage = ThreadMessage & { threadId: number };

// ``Omit`` e non un'intersezione: ``ThreadDetail & { messages: MergedMessage[] }``
// lascerebbe ``messages`` di tipo ``ThreadMessage[] & MergedMessage[]``, e
// l'accesso a ``threadId`` risolverebbe sul primo ramo — cioe' il campo che
// serve al separatore non sarebbe visibile ai chiamanti.
export type MergedThreadDetail = Omit<ThreadDetail, "messages"> & {
  messages: MergedMessage[];
  groupThreadIds: number[];
};

/** Primo elemento di una lista che il chiamante garantisce non vuota.
 *  ``noUncheckedIndexedAccess`` e' attivo: ``xs[0]`` e' ``T | undefined``, e
 *  un ``!`` qui nasconderebbe proprio il caso che vogliamo far esplodere. */
function primo<T>(xs: T[], dove: string): T {
  const testa = xs[0];
  if (testa === undefined) throw new Error(`${dove}: lista vuota`);
  return testa;
}

function chiaveGruppo(t: Pick<ThreadListItem, "account_id" | "external_order_id">): string | null {
  const ordine = (t.external_order_id ?? "").trim();
  // ⛔ ``external_order_id`` vuoto NON raggruppa. Il campo è Text default ""
  // per i thread generici (nessuna entità ORDER nel payload Mirakl): usarlo
  // come chiave fonderebbe fra loro TUTTI i thread senza ordine — un secondo
  // bug, più grosso di quello che stiamo risolvendo.
  return ordine ? `${t.account_id}::${ordine}` : null;
}

/** Millisecondi dell'ultimo messaggio; assente/illeggibile → in fondo. */
function quando(valore: string | null): number {
  if (!valore) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(valore);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/** Più recente prima; a parità di data decide l'id (deterministico). */
function piuRecentePrima<T extends { id: number; last_message_at: string | null }>(
  a: T,
  b: T,
): number {
  return quando(b.last_message_at) - quando(a.last_message_at) || b.id - a.id;
}

/** Il thread in cui parte la risposta: il più recente fra quelli che
 *  ASPETTANO risposta, altrimenti il più recente. Mirakl conta la risposta
 *  thread per thread: rispondere solo nel più recente lascia l'altro «in
 *  attesa del venditore», e la conversazione resta in «Da gestire». */
function destinatario<T extends { needs_reply: boolean }>(ordinati: T[], dove: string): T {
  return ordinati.find((t) => t.needs_reply) ?? primo(ordinati, dove);
}

function fondiRighe(gruppo: ThreadListItem[]): GroupedThread {
  const ordinati = [...gruppo].sort(piuRecentePrima);
  const capo = destinatario(ordinati, "fondiRighe");
  return {
    ...capo,
    // 🔑 ``id`` resta quella REALE del thread destinatario, mai un id
    // sintetico: è l'id con cui il backend posta la risposta su Mirakl
    // (``send_reply`` → /api/inbox/threads/{id}/message). Un id inventato
    // risponderebbe al thread sbagliato o romperebbe l'invio.
    message_count: ordinati.reduce((n, t) => n + t.message_count, 0),
    has_unread: ordinati.some((t) => t.has_unread),
    needs_reply: ordinati.some((t) => t.needs_reply),
    last_message_at: primo(ordinati, "fondiRighe").last_message_at,
    groupThreadIds: ordinati.map((t) => t.id),
  };
}

/**
 * Raggruppa per ``(account_id, external_order_id)``, preservando l'ordine
 * della lista in ingresso: il gruppo occupa la posizione del suo membro più
 * recente (la lista arriva già ordinata per ``last_message_at`` desc, quindi
 * è il primo che si incontra).
 */
export function groupThreadsByOrder(items: ThreadListItem[]): GroupedThread[] {
  const perChiave = new Map<string, ThreadListItem[]>();
  for (const t of items) {
    const k = chiaveGruppo(t);
    if (k === null) continue;
    const accumulati = perChiave.get(k);
    if (accumulati) accumulati.push(t);
    else perChiave.set(k, [t]);
  }

  const risultato: GroupedThread[] = [];
  const giaEmessi = new Set<string>();
  for (const t of items) {
    const k = chiaveGruppo(t);
    const gruppo = k === null ? undefined : perChiave.get(k);
    if (!gruppo || gruppo.length < 2) {
      risultato.push({ ...t, groupThreadIds: [t.id] });
      continue;
    }
    if (giaEmessi.has(k as string)) continue;
    giaEmessi.add(k as string);
    risultato.push(fondiRighe(gruppo));
  }
  return risultato;
}

/** Il gruppo che CONTIENE quel thread — per risolvere il deep-link
 *  ``?thread=ID``, che punta a un thread reale e non al rappresentativo. */
export function findGroupContaining(
  gruppi: GroupedThread[],
  threadId: number,
): GroupedThread | undefined {
  return gruppi.find((g) => g.groupThreadIds.includes(threadId));
}

/** Ordine cronologico dei messaggi in chat: dal più vecchio al più recente. */
function quandoMessaggio(m: MergedMessage): number {
  return quando(m.created_at_remote);
}

/**
 * Fonde i dettagli dei thread di un gruppo in una conversazione sola.
 *
 * I messaggi vengono concatenati, annotati col thread d'origine e ordinati per
 * ``created_at_remote`` (fallback ``id``, che è crescente nel tempo). L'``id``
 * del risultato è quella del thread destinatario — vedi ``destinatario``: è
 * l'id vero con cui parte la risposta.
 */
export function mergeThreadDetails(details: ThreadDetail[]): MergedThreadDetail {
  if (details.length === 0) {
    throw new Error("mergeThreadDetails: nessun dettaglio da fondere");
  }
  const ordinati = [...details].sort(piuRecentePrima);
  const capo = destinatario(ordinati, "mergeThreadDetails");
  const messaggi: MergedMessage[] = ordinati
    .flatMap((d) => d.messages.map((m) => ({ ...m, threadId: d.id })))
    .sort((a, b) => quandoMessaggio(a) - quandoMessaggio(b) || a.id - b.id);

  return {
    ...capo,
    messages: messaggi,
    message_count: messaggi.length,
    last_message_at: primo(ordinati, "mergeThreadDetails").last_message_at,
    has_unread: ordinati.some((d) => d.has_unread),
    needs_reply: ordinati.some((d) => d.needs_reply),
    groupThreadIds: ordinati.map((d) => d.id),
  };
}
